import { createSelector } from '@reduxjs/toolkit';

import { useAppSelector } from '../components/hooks';
import { RootState } from '../store';

export interface Breadcrumb {
  type: 'directory' | 'line' | 'station';

  name: string;
  guid: string;
}

type BreadcrumbState = Record<string, Breadcrumb[]> | 'loading' | 'error';

function useBreadcrumbs(key: string) {
  const breadcrumbs = useAppSelector(getAllBreadcrumbs);
  if (breadcrumbs === 'error' || breadcrumbs === 'loading') return [];

  return breadcrumbs[key] ?? [];
}

function getStationKey(lineGuid: string, stationGuid: string) {
  return `${lineGuid}.${stationGuid}`;
}

export const useDirectoryBreadcrumbs = useBreadcrumbs;
export const useLineBreadcrumbs = useBreadcrumbs;

export function useStationBreadcrumbs(lineGuid: string | undefined, stationGuid: string) {
  return useBreadcrumbs(lineGuid ? getStationKey(lineGuid, stationGuid) : stationGuid);
}

export function renderBreadcrumbsToString(breadcrumbs: Breadcrumb[], options?: { omitSelf?: boolean }): string {
  let relevantCrumbs = breadcrumbs;
  if (options?.omitSelf) relevantCrumbs = relevantCrumbs.slice(0, -1);

  return relevantCrumbs.map((crumb) => crumb.name).join(' / ');
}

export const getAllBreadcrumbs = createSelector(
  (state: RootState) => state.lines,
  (state: RootState) => state.stations,

  (linesState, stationsState): BreadcrumbState => {
    const statuses = [linesState.status, stationsState.status];

    if (statuses.includes('catastrophicallyFailed')) return 'error';
    if (statuses.includes('loading')) return 'loading';

    const result: Record<string, Breadcrumb[]> = {};

    function* getDirectoryBreadcrumbs(guid: string): Generator<Breadcrumb> {
      const directory = linesState.directories[guid];
      yield { type: 'directory', name: directory.name, guid: directory.guid };

      if (directory.parentGuid) {
        yield* getDirectoryBreadcrumbs(directory.parentGuid);
      }
    }

    function* getLineBreadcrumbs(guid: string): Generator<Breadcrumb> {
      const line = linesState.lines[guid];

      yield { type: 'line', name: line.name, guid: line.guid };

      if (line.parentGuid) {
        if (line.parentGuid in linesState.lines) {
          yield* getLineBreadcrumbs(line.parentGuid);
        } else if (line.parentGuid in linesState.directories) {
          yield* getDirectoryBreadcrumbs(line.parentGuid);
        }
      }
    }

    function* getStationBreadcrumbs(guid: string, lineGuid: string): Generator<Breadcrumb> {
      const station = stationsState.stations[guid];

      yield { type: 'station', name: station.name, guid: station.guid };
      yield* getLineBreadcrumbs(lineGuid);
    }

    for (const directory of Object.values(linesState.directories)) {
      if (directory.parentGuid) {
        result[directory.guid] = Array.from(getDirectoryBreadcrumbs(directory.guid)).toReversed();
      } else {
        result[directory.guid] = []; // an empty array signifies "no breadcrumbs" for a top level model
      }
    }

    for (const line of Object.values(linesState.lines)) {
      if (line.parentGuid) {
        result[line.guid] = Array.from(getLineBreadcrumbs(line.guid)).toReversed();
      } else {
        result[line.guid] = []; // an empty array signifies "no breadcrumbs" for a top level model
      }
    }

    for (const [lineGuid, stationInsertions] of Object.entries(linesState.stationInsertions)) {
      for (const insertion of stationInsertions) {
        result[getStationKey(lineGuid, insertion.guid)] = Array.from(
          getStationBreadcrumbs(insertion.guid, lineGuid),
        ).toReversed();
      }
    }

    // Stations that aren't assigned to a line still need an empty array to signify "no breadcrumbs."
    Object.values(stationsState.stations)
      .filter((station) => !(station.guid in result))
      .forEach((station) => {
        result[station.guid] = [];
      });

    return result;
  },
);
